import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { MarketingLayout } from "@/marketing/MarketingLayout";
import { Seo } from "@/components/Seo";
import { SectionLabel, IDButton, Monogram, TAGLINE } from "@/marketing/brand";
import api from "@/lib/api";
import { toast } from "sonner";
import { trackEvent } from "@/lib/track";
import { pixelTrack } from "@/lib/metaPixel";
import {
  BookOpen, HeartPulse, Briefcase, Coins, Users,
  ArrowLeft, ArrowRight, Check, Sparkles, RotateCcw, Clock, ListChecks, Footprints,
} from "lucide-react";

/*
 * /life-audit — The Life Audit (PUBLIC, no login).
 * A free, faith-centered reflection for Christian women. Warm, calm, emotionally
 * safe, premium. Flow: intro → assessment (5 pillars × 5 statements) → results.
 * Progress is preserved in localStorage so an accidental refresh never loses work.
 *
 * Language guardrails (never used): archetype, failure, failing, weak, unhealthy,
 * poor, at risk. Meaning is never communicated by color alone — every score band
 * carries a text label + icon + value, and every scale option shows a number + word.
 */

const STORAGE_KEY = "id_life_audit_v1";

const SCALE = [
  { v: 0, label: "Not yet" },
  { v: 1, label: "Rarely" },
  { v: 2, label: "Sometimes" },
  { v: 3, label: "Often" },
  { v: 4, label: "Consistently" },
];

const PILLARS = [
  {
    key: "disciple", name: "The Disciple", domain: "Faith & Devotion", icon: BookOpen,
    questions: [
      "I made space for prayer on ordinary days, not only during moments of crisis.",
      "I spent time in Scripture consistently enough for it to influence my choices.",
      "I responded to a conviction or prompting from God with obedience.",
      "I practiced worship, gratitude, confession, or Sabbath as part of my rhythm.",
      "My private choices reflected the faith I profess publicly.",
    ],
    step: "Choose one ordinary day this week for a short, unhurried time of prayer or Scripture — before the day asks anything of you.",
  },
  {
    key: "keeper", name: "The Keeper", domain: "Health & Vitality", icon: HeartPulse,
    questions: [
      "I protected a realistic window for sleep or restorative rest.",
      "I moved my body in ways appropriate for my current health and ability.",
      "I regularly chose food and water that supported my energy.",
      "I attended to a medical, emotional-health, or self-care need instead of repeatedly postponing it.",
      "I noticed and responded when my body needed rest, support, or a slower pace.",
    ],
    step: "Pick one restorative act — a realistic bedtime, a short walk, water on waking — and repeat it three days this week.",
  },
  {
    key: "builder", name: "The Builder", domain: "Work & Calling", icon: Briefcase,
    questions: [
      "I identified a small number of priorities and allowed them to guide my week.",
      "I completed meaningful work on the commitments that matter most in this season.",
      "I protected focused time from distractions and unnecessary urgency.",
      "I held at least one boundary that protected my calling, capacity, or time.",
      "I stopped, postponed, delegated, or declined work that did not belong in this season.",
    ],
    step: "Name your few real priorities for the week, and protect one focused hour for the work that matters most.",
  },
  {
    key: "steward", name: "The Steward", domain: "Wealth & Provision", icon: Coins,
    questions: [
      "I reviewed my spending rather than avoiding it.",
      "I took an intentional action toward saving, reducing debt, giving, or another financial priority.",
      "My spending choices generally reflected my values and current responsibilities.",
      "I practiced generosity within my actual capacity, without comparison or pressure.",
      "I made financial decisions with prayer, information, and intention rather than impulse or avoidance.",
    ],
    step: "Take one small, prayerful money step — review last week's spending, or set aside a modest amount toward a goal.",
  },
  {
    key: "anchor", name: "The Anchor", domain: "Relationships & Legacy", icon: Users,
    questions: [
      "I gave meaningful, undistracted attention to someone I care about.",
      "I maintained contact with people or a community that helps keep me grounded.",
      "I participated in church, service, or another form of shared life when I was able.",
      "I addressed or named a relationship concern instead of allowing it to quietly grow.",
      "I invested in a relationship or contribution that matters beyond immediate demands.",
    ],
    step: "Give one person your undistracted attention this week — a call, a shared meal, or an honest, unhurried conversation.",
  },
];

/* Gentle score bands (0–20 per pillar). No verdict language. */
function bandFor(score) {
  if (score >= 16) return { label: "Steady & flourishing", note: "Well-tended this season.", tone: "high" };
  if (score >= 11) return { label: "Steady, with room to grow", note: "A good foundation to build on.", tone: "mid" };
  if (score >= 6) return { label: "Asking for attention", note: "Ready for gentle tending this season.", tone: "low" };
  return { label: "Tender right now", note: "This calling is asking for care this season.", tone: "tender" };
}
/* Approved supporting state fills — pale, gentle, always paired with a text label. */
const TONE_CHIP = {
  high: "bg-[var(--id-state-steady)]",
  mid: "bg-[var(--id-state-progress)]",
  low: "bg-[var(--id-state-attention)]",
  tender: "bg-[var(--id-state-attention)]",
};

/* ============================ Accessible Likert statement ============================ */
function Statement({ pillarKey, qIndex, text, value, onChange }) {
  const groupName = `stmt-${pillarKey}-${qIndex}`;
  const legendId = `${groupName}-legend`;
  return (
    <div
      className="rounded-2xl border border-[color:rgba(32,32,32,0.10)] bg-white px-5 py-6 sm:px-7"
      data-testid={`la-statement-${pillarKey}-${qIndex}`}
    >
      <p id={legendId} className="id-sans flex gap-2.5 text-[15px] leading-relaxed text-[var(--id-charcoal)]/90">
        <span className="id-serif shrink-0 text-[17px] leading-[1.5] text-[var(--id-gold)]">{qIndex + 1}.</span>
        <span>{text}</span>
      </p>
      <div className="mt-5 grid grid-cols-5 gap-2 sm:gap-3" role="radiogroup" aria-labelledby={legendId}>
        {SCALE.map((opt) => {
          const selected = value === opt.v;
          return (
            <label
              key={opt.v}
              className={`relative flex min-h-[64px] cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border px-1 py-2.5 text-center transition-colors duration-150 focus-within:ring-2 focus-within:ring-[var(--id-gold)]/60 ${
                selected
                  ? "border-[var(--id-gold)] bg-[var(--id-gold)]/12 ring-1 ring-[var(--id-gold)]"
                  : "border-[color:rgba(32,32,32,0.12)] bg-white hover:border-[var(--id-gold)]/45"
              }`}
            >
              <input
                type="radio"
                name={groupName}
                value={opt.v}
                checked={selected}
                onChange={() => onChange(opt.v)}
                aria-label={`${opt.v} — ${opt.label}`}
                data-testid={`la-radio-${pillarKey}-${qIndex}-${opt.v}`}
                className="sr-only"
              />
              {selected && (
                <span className="absolute right-1 top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[var(--id-gold)] text-white" aria-hidden="true">
                  <Check size={10} strokeWidth={3} />
                </span>
              )}
              <span className={`id-serif text-xl leading-none ${selected ? "text-[var(--id-charcoal)]" : "text-[var(--id-charcoal)]/70"}`}>{opt.v}</span>
              <span className={`id-sans text-[10px] uppercase leading-tight tracking-[0.03em] ${selected ? "font-semibold text-[var(--id-charcoal)]" : "text-[var(--id-charcoal)]/50"}`}>{opt.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/* ============================ Focused layout for assessment/results ============================ */
const FocusFrame = ({ children }) => (
  <div className="id-surface min-h-screen">
    <Seo slug="life-audit" />
    <header className="border-b border-[color:rgba(32,32,32,0.10)] bg-[var(--id-ivory)]/80 backdrop-blur-sm">
      <div className="mx-auto flex max-w-[820px] items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/life-audit" aria-label="The Life Audit — home" className="flex items-center gap-2.5">
          <Monogram className="h-8" />
          <span className="id-sans text-[12px] font-semibold uppercase tracking-[0.24em] text-[var(--id-charcoal)]/60">The Life Audit</span>
        </Link>
        <span className="id-sans text-[12px] text-[var(--id-charcoal)]/45">Free · 10 minutes</span>
      </div>
    </header>
    <main className="mx-auto max-w-[820px] px-5 py-10 sm:px-8 sm:py-14">{children}</main>
  </div>
);

/* Results email capture — the assessment becomes a sign-up moment.
   Subscribes as source="life-audit" and passes the score + tender pillar so the
   owner can send a personalized "your results" email via a MailerLite automation. */
const ResultsSignup = ({ score, tenderPillar, results, nextStep }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Please enter your email.");
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email: email.trim(),
        name: name.trim(),
        source: "life-audit",
        audit_score: score,
        audit_tender_pillar: tenderPillar,
        audit_results: { ...(results || {}), next_step: (nextStep || "").trim() },
      });
      trackEvent("newsletter_signup");
      pixelTrack("CompleteRegistration", { content_name: "Life Audit", status: "completed", value: score }, { email: email.trim() });
      setDone(data.message);
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please enter a valid email.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="life-audit-signup-success" className="mx-auto max-w-[440px] rounded-2xl border border-[color:rgba(32,32,32,0.10)] bg-[var(--id-ivory)] px-6 py-6 text-center">
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[var(--id-gold)]/15 text-[var(--id-gold)]">
          <Check size={22} strokeWidth={2} />
        </div>
        <p className="id-serif mt-4 text-xl text-[var(--id-charcoal)]">{done}</p>
        <p className="id-sans mt-2 text-sm text-[var(--id-charcoal)]/55">Watch your inbox — your first Sunday Reset comes this Sunday.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="life-audit-signup-form" className="mx-auto mt-7 max-w-[520px] text-left">
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="First name (optional)" data-testid="life-audit-signup-name"
          className="id-sans w-full rounded-xl border border-[color:rgba(32,32,32,0.15)] bg-white px-4 py-3 text-[15px] text-[var(--id-charcoal)] placeholder:text-[var(--id-charcoal)]/40 outline-none focus:border-[var(--id-gold)] focus:ring-2 focus:ring-[var(--id-gold)]/30"
        />
        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address" required data-testid="life-audit-signup-email"
          className="id-sans w-full rounded-xl border border-[color:rgba(32,32,32,0.15)] bg-white px-4 py-3 text-[15px] text-[var(--id-charcoal)] placeholder:text-[var(--id-charcoal)]/40 outline-none focus:border-[var(--id-gold)] focus:ring-2 focus:ring-[var(--id-gold)]/30"
        />
      </div>
      <IDButton as="button" type="submit" variant="solid" disabled={busy} testid="life-audit-signup-submit" className="mt-3 w-full !py-3.5">
        {busy ? "Sending…" : "Email me my results & next step"} <ArrowRight size={16} />
      </IDButton>
      <p className="id-sans mt-3 text-center text-[12.5px] text-[var(--id-charcoal)]/45">
        Free weekly letter. Grace over shame. Unsubscribe anytime.
      </p>
    </form>
  );
};

/* ================================= Main component ================================= */
export default function LifeAudit() {
  const [phase, setPhase] = useState("intro"); // intro | assessment | results
  const [step, setStep] = useState(0); // pillar index 0..4
  const [answers, setAnswers] = useState({}); // key `${pillarKey}_${qi}` -> 0..4
  const [nextStepNote, setNextStepNote] = useState("");
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const topRef = useRef(null);

  // Restore any saved progress on first mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s && typeof s === "object") {
          if (s.answers) setAnswers(s.answers);
          if (typeof s.step === "number") setStep(Math.min(Math.max(s.step, 0), PILLARS.length - 1));
          if (s.phase === "assessment" || s.phase === "results") setPhase(s.phase);
          if (typeof s.nextStepNote === "string") setNextStepNote(s.nextStepNote);
        }
      }
    } catch (e) { /* ignore corrupt storage */ }
    setHydrated(true);
  }, []);

  // Persist progress.
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ phase, step, answers, nextStepNote }));
    } catch (e) { /* storage may be unavailable */ }
  }, [phase, step, answers, nextStepNote, hydrated]);

  const scrollTop = useCallback(() => {
    if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const setAnswer = (pillarKey, qi, v) => {
    setShowIncomplete(false);
    setAnswers((prev) => ({ ...prev, [`${pillarKey}_${qi}`]: v }));
  };

  const pillar = PILLARS[step];
  const pillarComplete = (p) => p.questions.every((_, qi) => typeof answers[`${p.key}_${qi}`] === "number");

  const start = () => {
    // Meta: someone began the audit — lets you retarget those who start but don't finish.
    pixelTrack("ViewContent", { content_name: "Life Audit", content_category: "assessment" });
    setPhase("assessment"); setStep(0); scrollTop();
  };
  const goBack = () => {
    if (step === 0) { setPhase("intro"); }
    else { setStep((s) => s - 1); }
    setShowIncomplete(false);
    scrollTop();
  };
  const goContinue = () => {
    if (!pillarComplete(pillar)) { setShowIncomplete(true); return; }
    if (step < PILLARS.length - 1) { setStep((s) => s + 1); scrollTop(); }
    else { trackEvent("life_audit_complete"); setPhase("results"); scrollTop(); }
  };
  const restart = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    setAnswers({}); setStep(0); setNextStepNote(""); setShowIncomplete(false); setPhase("intro"); scrollTop();
  };

  // Scores
  const pillarScore = (p) => p.questions.reduce((sum, _, qi) => sum + (answers[`${p.key}_${qi}`] || 0), 0);
  const total = PILLARS.reduce((sum, p) => sum + pillarScore(p), 0); // 0..100
  const lowestPillar = [...PILLARS].sort((a, b) => pillarScore(a) - pillarScore(b))[0];

  if (!hydrated) return <div className="id-surface min-h-screen" aria-busy="true" />;

  /* ------------------------------- INTRO / LANDING ------------------------------- */
  if (phase === "intro") {
    return (
      <MarketingLayout>
        <Seo slug="life-audit" />
        <div data-testid="life-audit-page">
          {/* Hero */}
          <section className="mx-auto max-w-[1280px] px-5 pt-14 pb-14 sm:px-8 sm:pt-20 lg:pt-24 lg:pb-16">
            <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
              {/* Left — message + CTA */}
              <div className="text-center lg:text-left">
                <SectionLabel className="!tracking-[0.28em]">A free 10-minute reflection for Christian women</SectionLabel>
                <h1 className="id-serif mt-6 text-[2.4rem] leading-[1.05] text-[var(--id-charcoal)] sm:text-[3.5rem] lg:text-[4.25rem] lg:leading-[1.02]">
                  Your life may not need a complete overhaul. It may need one honest look.
                </h1>
                <p className="id-sans mx-auto mt-6 max-w-[540px] text-[18px] leading-relaxed text-[var(--id-charcoal)]/70 lg:mx-0">
                  The Life Audit helps you see what is steady, what needs tending, and where one faithful
                  next step could create meaningful relief.
                </p>
                <p className="id-serif mt-6 text-2xl text-[var(--id-walnut)]">Ten minutes. Five pillars. One honest score.</p>
                <div className="mt-8 flex justify-center lg:justify-start">
                  <IDButton as="button" variant="solid" onClick={start} testid="life-audit-start-btn" className="!px-9 !py-4 text-[15px]">
                    Take the Free Life Audit <ArrowRight size={17} />
                  </IDButton>
                </div>
                <p className="id-sans mx-auto mt-5 max-w-[520px] text-[13.5px] leading-relaxed text-[var(--id-charcoal)]/50 lg:mx-0">
                  Your results are a snapshot of this season — not a verdict on your faith, discipline,
                  success, or worth.
                </p>
              </div>
              {/* Right — promise stat panel */}
              <div className="rounded-[28px] border border-[color:rgba(32,32,32,0.10)] bg-white px-8 py-6 shadow-[0_44px_90px_-45px_rgba(43,38,34,0.45)] sm:px-11 sm:py-8">
                {[
                  { n: "10", u: "minutes", d: "An unhurried, honest reflection on the last thirty days." },
                  { n: "5", u: "life pillars", d: "Faith, health, work, wealth, and relationships — your whole life." },
                  { n: "1", u: "honest score", d: "A compassionate snapshot of this season, never a verdict." },
                ].map((s, i) => (
                  <div key={s.u} className={`flex items-baseline gap-6 py-6 ${i < 2 ? "border-b border-[color:rgba(32,32,32,0.08)]" : ""}`}>
                    <span className="id-serif text-6xl leading-none text-[var(--id-gold)]">{s.n}</span>
                    <div>
                      <p className="id-serif text-2xl text-[var(--id-charcoal)]">{s.u}</p>
                      <p className="id-sans mt-1 text-[14.5px] leading-relaxed text-[var(--id-charcoal)]/60">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Five pillars */}
          <section className="mx-auto max-w-[1280px] px-5 py-14 sm:px-8 sm:py-16">
            <div className="mx-auto max-w-[620px] text-center">
              <SectionLabel>The five callings you carry</SectionLabel>
              <h2 className="id-serif mt-4 text-[2.1rem] text-[var(--id-charcoal)] sm:text-[2.75rem]">Five Life Pillars, one whole life</h2>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
              {PILLARS.map((p) => {
                const Icon = p.icon;
                return (
                  <div key={p.key} className="rounded-2xl border border-[color:rgba(32,32,32,0.10)] bg-white px-6 py-7 text-center">
                    <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--id-gold)]/12 text-[var(--id-gold)]">
                      <Icon size={20} strokeWidth={1.8} />
                    </span>
                    <h3 className="id-serif mt-4 text-2xl text-[var(--id-charcoal)]">{p.name}</h3>
                    <p className="id-sans mt-1 text-[13px] text-[var(--id-charcoal)]/55">{p.domain}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* How it works */}
          <section className="mx-auto max-w-[1280px] px-5 pb-20 pt-6 sm:px-8 sm:pb-28">
            <div className="rounded-3xl border border-[color:rgba(32,32,32,0.10)] bg-[var(--id-ivory)] px-6 py-12 sm:px-14 sm:py-14">
              <div className="mx-auto max-w-[620px] text-center">
                <SectionLabel>How it works</SectionLabel>
                <h2 className="id-serif mt-4 text-[2.1rem] text-[var(--id-charcoal)] sm:text-[2.75rem]">Three unhurried steps</h2>
              </div>
              <ol className="mx-auto mt-10 grid max-w-[900px] gap-6 sm:grid-cols-3">
                {[
                  { icon: Clock, t: "Reflect honestly", d: "Answer for the past 30 days — a gentle, honest look at how this season has actually gone." },
                  { icon: ListChecks, t: "Receive your score", d: "See a clear, compassionate score across all five Life Pillars — a snapshot, not a verdict." },
                  { icon: Footprints, t: "Choose one next step", d: "Leave with a single faithful next step for the coming week. Just one — that's the point." },
                ].map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <li key={s.t} className="rounded-2xl bg-white px-6 py-7 text-center">
                      <span className="id-serif text-sm font-semibold text-[var(--id-gold)]">Step {i + 1}</span>
                      <span className="mx-auto mt-3 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--id-gold)]/12 text-[var(--id-gold)]">
                        <Icon size={20} strokeWidth={1.8} />
                      </span>
                      <h3 className="id-serif mt-4 text-2xl text-[var(--id-charcoal)]">{s.t}</h3>
                      <p className="id-sans mt-2 text-[14px] leading-relaxed text-[var(--id-charcoal)]/65">{s.d}</p>
                    </li>
                  );
                })}
              </ol>
              <div className="mt-10 flex justify-center">
                <IDButton as="button" variant="solid" onClick={start} testid="life-audit-start-btn-2" className="!px-8 !py-3.5 text-[14px]">
                  Take the Free Life Audit <ArrowRight size={16} />
                </IDButton>
              </div>
            </div>
          </section>
        </div>
      </MarketingLayout>
    );
  }

  /* ------------------------------- ASSESSMENT ------------------------------- */
  if (phase === "assessment") {
    const Icon = pillar.icon;
    const isLast = step === PILLARS.length - 1;
    return (
      <FocusFrame>
        <div ref={topRef} data-testid="life-audit-assessment">
          {/* Progress */}
          <div className="flex items-center justify-between">
            <p className="id-sans text-[12px] font-semibold uppercase tracking-[0.22em] text-[var(--id-charcoal)]/50" data-testid="life-audit-progress" aria-live="polite">
              Pillar {step + 1} of {PILLARS.length}
            </p>
            <span className="id-sans text-[12px] text-[var(--id-charcoal)]/45">{step + 1} / {PILLARS.length}</span>
          </div>
          <div className="mt-3 flex gap-1.5" aria-hidden="true">
            {PILLARS.map((p, i) => (
              <span key={p.key} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[var(--id-gold)]" : "bg-[color:rgba(32,32,32,0.10)]"}`} />
            ))}
          </div>

          {/* Pillar header */}
          <div className="mt-8 flex items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--id-gold)]/12 text-[var(--id-gold)]">
              <Icon size={24} strokeWidth={1.7} />
            </span>
            <div>
              <h1 className="id-serif text-3xl leading-tight text-[var(--id-charcoal)] sm:text-4xl">{pillar.name}</h1>
              <p className="id-sans text-[14px] text-[var(--id-charcoal)]/55">{pillar.domain}</p>
            </div>
          </div>

          {/* Instruction + scale legend */}
          <div className="mt-6 rounded-2xl bg-[var(--id-ivory)] px-5 py-4">
            <p className="id-sans text-[14px] leading-relaxed text-[var(--id-charcoal)]/75">
              Answer for the past 30 days — not for the woman you hoped to be or think you should be.
            </p>
            <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[var(--id-charcoal)]/55">
              {SCALE.map((o) => (
                <span key={o.v} className="id-sans"><span className="id-serif text-[var(--id-gold)]">{o.v}</span> {o.label}</span>
              ))}
            </dl>
          </div>

          {/* Statements */}
          <div className="mt-6 space-y-4">
            {pillar.questions.map((q, qi) => (
              <Statement
                key={qi}
                pillarKey={pillar.key}
                qIndex={qi}
                text={q}
                value={answers[`${pillar.key}_${qi}`]}
                onChange={(v) => setAnswer(pillar.key, qi, v)}
              />
            ))}
          </div>

          {/* Incomplete notice */}
          {showIncomplete && (
            <p role="status" data-testid="life-audit-incomplete-msg" className="mt-5 rounded-xl border border-[var(--id-gold)]/40 bg-[var(--id-gold)]/8 px-4 py-3 text-[14px] text-[var(--id-walnut)]">
              Gently — please answer all five to continue. There are no wrong answers here.
            </p>
          )}

          {/* Navigation */}
          <div className="mt-8 flex items-center justify-between gap-4">
            <IDButton as="button" variant="ghost" onClick={goBack} testid="life-audit-back-btn" className="!px-6">
              <ArrowLeft size={16} /> Back
            </IDButton>
            <IDButton as="button" variant="solid" onClick={goContinue} testid="life-audit-continue-btn" className="!px-7">
              {isLast ? "See my results" : "Continue"} <ArrowRight size={16} />
            </IDButton>
          </div>
        </div>
      </FocusFrame>
    );
  }

  /* ------------------------------- RESULTS ------------------------------- */
  return (
    <FocusFrame>
      <div ref={topRef} data-testid="life-audit-results">
        <div className="text-center">
          <SectionLabel>Your Life Audit</SectionLabel>
          <h1 className="id-serif mt-4 text-4xl text-[var(--id-charcoal)] sm:text-5xl">A snapshot of this season</h1>
          <div className="mx-auto mt-8 max-w-[420px] rounded-3xl border border-[color:rgba(32,32,32,0.10)] bg-white px-6 py-8">
            <p className="id-sans text-[12px] uppercase tracking-[0.22em] text-[var(--id-charcoal)]/45">One honest score</p>
            <p className="id-serif mt-2 text-[4rem] leading-none text-[var(--id-charcoal)]" data-testid="life-audit-total-score">
              {total}<span className="text-2xl text-[var(--id-charcoal)]/40"> / 100</span>
            </p>
            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[color:rgba(32,32,32,0.10)]" aria-hidden="true">
              <div className="h-full rounded-full bg-[var(--id-gold)]" style={{ width: `${total}%` }} />
            </div>
          </div>
        </div>

        {/* Mandated framing */}
        <div className="mx-auto mt-8 max-w-[640px] rounded-2xl bg-[var(--id-ivory)] px-6 py-6 text-center">
          <p className="id-serif text-xl leading-relaxed text-[var(--id-charcoal)]">
            You carry all five callings. Different pillars require different levels of attention in different seasons.
          </p>
        </div>

        {/* Per-pillar breakdown */}
        <div className="mt-10 space-y-3">
          {PILLARS.map((p) => {
            const sc = pillarScore(p);
            const band = bandFor(sc);
            const Icon = p.icon;
            return (
              <div key={p.key} data-testid={`pillar-result-${p.key}`} className="rounded-2xl border border-[color:rgba(32,32,32,0.10)] bg-white px-5 py-5 sm:px-6">
                <div className="flex items-center gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--id-gold)]/12 text-[var(--id-gold)]">
                    <Icon size={18} strokeWidth={1.8} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <h3 className="id-serif text-lg text-[var(--id-charcoal)]">{p.name} <span className="id-sans text-[12px] font-normal text-[var(--id-charcoal)]/45">· {p.domain}</span></h3>
                      <span className="id-sans text-[13px] text-[var(--id-charcoal)]/55">{sc} / 20</span>
                    </div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[color:rgba(32,32,32,0.10)]" aria-hidden="true">
                      <div className="h-full rounded-full bg-[var(--id-gold)]" style={{ width: `${(sc / 20) * 100}%` }} />
                    </div>
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      <span className={`id-sans inline-flex items-center rounded-full px-2.5 py-0.5 text-[12px] font-semibold text-[var(--id-charcoal)] ${TONE_CHIP[band.tone]}`}>{band.label}</span>
                      <span className="id-sans text-[13px] text-[var(--id-charcoal)]/60">{band.note}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* One faithful next step */}
        <div className="mt-10 overflow-hidden rounded-3xl bg-[var(--id-walnut)] px-6 py-10 text-[var(--id-ivory)] sm:px-12 sm:py-12">
          <span className="id-sans inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--id-gold)]">
            <Sparkles size={13} /> One faithful next step
          </span>
          <h2 className="id-serif mt-5 text-2xl leading-snug sm:text-3xl">
            This season, <span className="text-[var(--id-gold)]">{lowestPillar.name}</span> may be asking for your attention.
          </h2>
          <p className="id-sans mt-4 max-w-[560px] text-[15px] leading-relaxed text-[var(--id-ivory)]/80">{lowestPillar.step}</p>
          <label htmlFor="next-step-note" className="id-sans mt-7 block text-[12px] uppercase tracking-[0.18em] text-[var(--id-ivory)]/55">
            Name your one faithful next step (optional)
          </label>
          <textarea
            id="next-step-note"
            data-testid="life-audit-nextstep-note"
            value={nextStepNote}
            onChange={(e) => setNextStepNote(e.target.value)}
            rows={2}
            placeholder="This week, I will…"
            className="id-sans mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-[14px] text-[var(--id-ivory)] placeholder:text-[var(--id-ivory)]/40 outline-none focus:ring-2 focus:ring-[var(--id-gold)]"
          />
        </div>

        {/* Sign-up moment: email me my results + gentle funnel */}
        <div className="mt-10 rounded-3xl border border-[color:rgba(32,32,32,0.10)] bg-white px-6 py-10 text-center sm:px-12">
          <Monogram className="mx-auto h-10" />
          <h2 className="id-serif mt-5 text-2xl text-[var(--id-charcoal)] sm:text-3xl">Want to keep this — and take one step at a time?</h2>
          <p className="id-sans mx-auto mt-3 max-w-[520px] text-[15px] leading-relaxed text-[var(--id-charcoal)]/65">
            Get your results and one faithful next step by email, then a free weekly Sunday Reset — a word
            to center on, a gentle reset, and one pillar to tend.
          </p>
          <ResultsSignup
            score={total}
            tenderPillar={lowestPillar.name}
            nextStep={nextStepNote}
            results={{
              score: total,
              tender_pillar: lowestPillar.name,
              tender_domain: lowestPillar.domain,
              tender_step: lowestPillar.step,
              pillars: PILLARS.map((p) => {
                const sc = pillarScore(p);
                const band = bandFor(sc);
                return { name: p.name, domain: p.domain, score: sc, band: band.label, note: band.note };
              }),
            }}
          />
          <button onClick={restart} data-testid="life-audit-restart" className="id-sans mt-6 inline-flex items-center gap-1.5 text-[13px] text-[var(--id-charcoal)]/55 underline-offset-4 hover:text-[var(--id-charcoal)] hover:underline">
            <RotateCcw size={14} /> Retake the Life Audit
          </button>
          <p className="id-sans mx-auto mt-7 max-w-[520px] text-[13px] leading-relaxed text-[var(--id-charcoal)]/45">
            Remember: this is a snapshot of one season — not a verdict on your faith, discipline, success, or worth.
          </p>
          <p className="id-serif mt-4 text-lg text-[var(--id-walnut)]">{TAGLINE}</p>
        </div>
      </div>
    </FocusFrame>
  );
}
